import { faPeopleRoof, faUser } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { Accordion, Col, Row } from 'react-bootstrap-5';
import { connect, useSelector } from 'react-redux';
import RefreshOwnersModal from './refresh_owners_modal';
import EmptyState from '../../empty_state';
import AccordionOwnerList from './accordion_owner_list';
import TabbedOwnerList from './tabbed_owner_list';
import { setOwners } from 'bundles/connect/components/store/actions/owners';
import type { Ownerships, OwnershipsResponse } from 'bundles/connect/types';
import HighestConfidenceOwners from './highest_confidence_owners';
import classNames from 'classnames';
import { selectHasUnlimitedUnlocks } from '../../store/selectors/ui';

type Props = {
  insightsPropertyId: string;
  owners: Ownerships[];
  remainingCredits: number;
  setOwners: (results: OwnershipsResponse) => void;
  setRemainingCredits: React.Dispatch<React.SetStateAction<number>>;
  show: boolean;
}

function ContactSection(
  { insightsPropertyId, remainingCredits, setOwners, setRemainingCredits, owners, show }: Props
) {
  const [activeKey, setActiveKey] = useState('');
  const [accordionKey, setAccordionKey] = useState<string | null>(null);
  const unlimitedUnlocks = useSelector(selectHasUnlimitedUnlocks);

  const hasOwners = owners && owners.length > 0;

  useEffect(() => {
    if (owners && !owners.find(owner => owner.id === parseInt(activeKey))) setActiveKey(String(owners[0]?.id));
  }, [owners, activeKey]);

  const COMMON_OWNER_LIST_PROPS = { activeKey, owners, setActiveKey, insightsPropertyId };
  const COMMON_PROPS = {
    insightsPropertyId,
    remainingCredits,
    onUnlock: setOwners,
    setRemainingCredits
  };

  const highestConfidenceOwners = owners.filter((owner) => {
    const hasFormattedPhone = Array.isArray(owner.contact.phoneNumbers) &&
      owner.contact.phoneNumbers.some(phone => phone.formatted);

    const isVerified = owner.llcMatch || owner.aiVerified || owner.assessorMatch;

    return hasFormattedPhone && isVerified;
  });

  const toggleAccordion = () => {
    if (accordionKey == '0') {
      setAccordionKey(null);
    } else {
      setAccordionKey('0');
    }
  };

  const handleKeyboardAccordion = (e: React.KeyboardEvent) => {
    if (['Tab', 'Escape'].includes(e.key)) {
      return;
    }
    e.preventDefault();

    if (['Enter', ' '].includes(e.key)) {
      toggleAccordion();
    }
  };

  return (
    <div>
      <Row className="align-items-center">
        <Col>
          <div className="d-flex gap-2 align-items-center">
            <FontAwesomeIcon className="text-primary" icon={faPeopleRoof} />
            <h6 className="my-0 fw-bolder">Contacts</h6>
          </div>
        </Col>
        {!unlimitedUnlocks && <RefreshOwnersModal {...COMMON_PROPS} visible={show} />}
      </Row>
      {owners && owners.length === 0 && (
        <EmptyState icon={faUser} title={'No Contacts Found'} />
      )}
      {hasOwners && (
        <div className="d-flex flex-column gap-3">
          <HighestConfidenceOwners owners={highestConfidenceOwners} />
          <div className="d-block d-lg-none">
            <AccordionOwnerList {...COMMON_OWNER_LIST_PROPS} />
          </div>
          <div className="d-none d-lg-block">
            {highestConfidenceOwners.length > 0 ? (
              <Accordion activeKey={accordionKey}>
                <Accordion.Item className="border-0" eventKey="0">
                  <h2 className="accordion-header" onClick={toggleAccordion}>
                    <div
                      className={classNames(
                        'd-flex',
                        'accordion-button',
                        'bg-white',
                        'border-0',
                        'px-0',
                        { collapsed: accordionKey === null }
                      )}
                      role="button"
                      style={{ boxShadow: 'none', outline: 'none', backgroundColor: 'transparent' }}
                      tabIndex={0}
                      onKeyDown={handleKeyboardAccordion}
                    >
                      <div className="d-flex align-items-center justify-content-between w-100">
                        <div className="fw-bold fs-large" id="contactsAccordion">
                          All contacts ({owners.length})
                        </div>
                      </div>
                    </div>
                  </h2>
                  <Accordion.Body className="p-0">
                    <TabbedOwnerList {...COMMON_OWNER_LIST_PROPS} />
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            ) : (
              <TabbedOwnerList {...COMMON_OWNER_LIST_PROPS} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const mapStateToProps = ({ offMarketProperty }) => ({ owners: offMarketProperty.owners });
export default connect(mapStateToProps, { setOwners })(ContactSection);
